import React, { useState } from 'react';
import TeslaRobot from '../components/TeslaRobot';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setFormStatus('submitting');
    
    // Simulate API call
    try {
      // In a real application, this would be an API call to your backend
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: ''
      });
      
      setFormStatus('success');
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setFormStatus('idle');
      }, 5000);
    } catch (error) {
      setFormStatus('error');
      
      // Reset error message after 5 seconds
      setTimeout(() => {
        setFormStatus('idle');
      }, 5000);
    }
  };
  
  return (
    <div className="contact-page">
      <div className="contact-header">
        <div className="contact-title-container">
          <h1 className="contact-title">
            Contact <span className="text-gradient">Us</span>
          </h1>
          <p className="contact-subtitle">
            Get in touch with our team to learn more about Digital Workplace
          </p>
        </div>
        <div className="contact-robot">
          <TeslaRobot width={120} height={120} />
        </div>
      </div>
      
      <div className="contact-content">
        <div className="contact-form-container">
          <h2>Send us a message</h2>
          
          {formStatus === 'success' && (
            <div className="contact-success">
              <div className="success-icon">✓</div>
              <div className="success-message">
                <h3>Message Sent!</h3>
                <p>Thank you for contacting us. We'll get back to you shortly.</p>
              </div>
            </div>
          )}
          
          {formStatus === 'error' && (
            <div className="contact-error">
              <div className="error-icon">!</div>
              <div className="error-message">
                <h3>Something went wrong</h3>
                <p>Please try again or contact us directly at support@digitalworkplace.com</p>
              </div>
            </div>
          )}
          
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your full name"
                  className="contact-input"
                  disabled={formStatus === 'submitting'}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter your email address"
                  className="contact-input"
                  disabled={formStatus === 'submitting'}
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Enter your company name (optional)"
                  className="contact-input"
                  disabled={formStatus === 'submitting'}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="contact-input"
                  disabled={formStatus === 'submitting'}
                >
                  <option value="">Select a subject</option>
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Sales">Sales</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="API Integration">API Integration</option>
                  <option value="Partnership">Partnership</option>
                </select>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                placeholder="Enter your message"
                className="contact-input contact-textarea"
                rows={6}
                disabled={formStatus === 'submitting'}
              ></textarea>
            </div>
            
            <button 
              type="submit" 
              className="contact-submit-button"
              disabled={formStatus === 'submitting'}
            >
              {formStatus === 'submitting' ? (
                <span className="loading-spinner"></span>
              ) : 'Send Message'}
            </button>
          </form>
        </div>
        
        <div className="contact-info-container">
          <div className="contact-info-card">
            <h3>Contact Information</h3>
            <div className="contact-info-item">
              <div className="contact-icon">📧</div>
              <div className="contact-detail">
                <h4>Email</h4>
                <p><a href="mailto:info@digitalworkplace.com">info@digitalworkplace.com</a></p>
              </div>
            </div>
            
            <div className="contact-info-item">
              <div className="contact-icon">📞</div>
              <div className="contact-detail">
                <h4>Phone</h4>
                <p><a href="tel:+18005551234">+1 (800) 555-1234</a></p>
              </div>
            </div>
            
            <div className="contact-info-item">
              <div className="contact-icon">🏢</div>
              <div className="contact-detail">
                <h4>Headquarters</h4>
                <p>123 AI Boulevard<br />San Francisco, CA 94105<br />United States</p>
              </div>
            </div>
            
            <div className="contact-info-item">
              <div className="contact-icon">⏰</div>
              <div className="contact-detail">
                <h4>Business Hours</h4>
                <p>Monday - Friday: 9:00 AM - 6:00 PM PST</p>
              </div>
            </div>
          </div>
          
          <div className="contact-social">
            <h3>Connect With Us</h3>
            <div className="social-links">
              <a href="#" className="social-link">
                <div className="social-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                    <rect x="2" y="9" width="4" height="12"></rect>
                    <circle cx="4" cy="4" r="2"></circle>
                  </svg>
                </div>
                <span>LinkedIn</span>
              </a>
              
              <a href="#" className="social-link">
                <div className="social-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
                  </svg>
                </div>
                <span>Twitter</span>
              </a>
              
              <a href="#" className="social-link">
                <div className="social-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </div>
                <span>Instagram</span>
              </a>
              
              <a href="#" className="social-link">
                <div className="social-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                  </svg>
                </div>
                <span>Facebook</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <div className="contact-faq">
        <h2 className="faq-title">Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h3>How quickly can I get started with Digital Workplace?</h3>
            <p>
              You can get started immediately after creating an account. Our digital employees 
              are ready to work right away, with no complex setup or training required.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>Can I customize my digital employees?</h3>
            <p>
              Yes, you can customize your digital employees based on the skills, industries, 
              and specific requirements of your business.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>How does billing work?</h3>
            <p>
              We offer flexible hourly rates for each digital employee. You only pay for the 
              time you use, with no long-term commitments required.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>Is my data secure?</h3>
            <p>
              Yes, we take data security very seriously. All communications are encrypted, 
              and we adhere to strict privacy policies to protect your information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
